import { useState } from "react";
import { NavLink } from "react-router-dom";
import { Menu } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import { cn } from "@/lib/cn";
import { useWaitlist } from "@/components/WaitlistContext";
import { ThemeToggle } from "@/components/ThemeToggle";

const NAV = [
  { to: "/for", label: "For your role" },
  { to: "/integrations", label: "Integrations" },
  { to: "/vs/openclaw", label: "vs OpenClaw" },
  { to: "/pricing", label: "Pricing" },
  { to: "/blog", label: "Blog" },
];

/**
 * Top navigation. Uses <nav aria-label="Primary"> to distinguish from the
 * footer nav, and a mobile Sheet for <768px. Active links get aria-current.
 */
export function Header() {
  const [open, setOpen] = useState(false);
  const { openWaitlist } = useWaitlist();

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border/70 bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between">
        <Logo />

        <nav aria-label="Primary" className="hidden md:block">
          <ul className="flex items-center gap-1">
            {NAV.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    cn(
                      "inline-flex h-11 items-center rounded-md px-3 text-sm font-medium text-foreground/80 transition-colors hover:text-foreground hover:bg-accent",
                      isActive && "text-foreground bg-accent",
                    )
                  }
                >
                  {({ isActive }) => (
                    <span aria-current={isActive ? "page" : undefined}>{item.label}</span>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <ThemeToggle />
          <Button variant="ghost" size="sm" asChild>
            <a href="#login">Sign in</a>
          </Button>
          <Button size="sm" onClick={() => openWaitlist()}>
            Get started
          </Button>
        </div>

        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="md:hidden" aria-label="Open menu">
              <Menu aria-hidden="true" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right">
            <div className="mb-6 flex items-center justify-between">
              <SheetTitle>Navigation</SheetTitle>
              <ThemeToggle />
            </div>
            <nav aria-label="Mobile">
              <ul className="flex flex-col gap-2">
                {NAV.map((item) => (
                  <li key={item.to}>
                    <SheetClose asChild>
                      <NavLink
                        to={item.to}
                        className={({ isActive }) =>
                          cn(
                            "flex h-11 items-center rounded-md px-3 text-base font-medium hover:bg-accent",
                            isActive && "bg-accent",
                          )
                        }
                      >
                        {({ isActive }) => (
                          <span aria-current={isActive ? "page" : undefined}>{item.label}</span>
                        )}
                      </NavLink>
                    </SheetClose>
                  </li>
                ))}
                <li className="mt-4">
                  <SheetClose asChild>
                    <Button className="w-full" onClick={() => openWaitlist()}>
                      Get started
                    </Button>
                  </SheetClose>
                </li>
              </ul>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
